'use client';

import { motion } from 'motion/react';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Clock,
  MessageSquare,
  Linkedin,
  Twitter,
} from 'lucide-react';
import { useState } from 'react';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock form submission
    alert("Thank you for your message! We'll get back to you within 24 hours.");
    setFormData({ name: '', email: '', company: '', subject: '', message: '' });
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      content: 'support@leadbunker.io',
      action: 'mailto:support@leadbunker.io',
    },
    {
      icon: Phone,
      title: 'Call Us',
      content: '+1 (555) 123-4567',
      action: 'tel:+15551234567',
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      content: '123 Innovation Drive, San Francisco, CA 94105',
      action: null,
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Mon-Fri: 9AM - 6PM PST',
      action: null,
    },
  ];

  const departments = [
    {
      title: 'Sales Inquiries',
      description: 'Learn about our solutions and pricing',
      email: 'sales@leadbunker.io',
    },
    {
      title: 'Technical Support',
      description: 'Get help with your account or technical issues',
      email: 'support@leadbunker.io',
    },
    {
      title: 'Partnership',
      description: 'Explore partnership opportunities',
      email: 'partners@leadbunker.io',
    },
    {
      title: 'Media & Press',
      description: 'Press inquiries and media relations',
      email: 'press@leadbunker.io',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="px-4 py-20 md:py-32 relative overflow-hidden bg-linear-to-br from-background-secondary via-secondary to-accent dark:from-[#1e293b] dark:via-[#312e81] dark:to-[#3730a3]">
        {/* Animated Background Elements */}
        <motion.div
          className="absolute top-20 right-10 w-72 h-72 bg-linear-to-br from-primary/40 to-[#8b5cf6]/30 rounded-full opacity-60 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 30, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-20 left-10 w-96 h-96 bg-linear-to-tr from-[#8b5cf6]/20 to-[#ec4899]/20 rounded-full opacity-60 blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -40, 0],
            y: [0, 40, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iY3VycmVudENvbG9yIiBzdHJva2Utb3BhY2l0eT0iMC4xIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30 text-primary dark:opacity-20"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-card/80 backdrop-blur-sm border border-card-border rounded-full mb-6 shadow-lg">
              <MessageSquare className="w-5 h-5 text-primary" />
              <span className="text-primary">Contact Us</span>
            </div>

            <h1 className="mb-6 text-foreground">Get In Touch</h1>
            <p className="text-foreground-secondary max-w-2xl mx-auto mb-8">
              Have questions about Leadbunker? Our team is here to help you
              succeed. Reach out and we&apos;ll respond within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-card border border-card-border rounded-2xl p-8">
                <h2 className="mb-6 text-foreground">Send Us a Message</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-foreground mb-2"
                    >
                      Full Name *
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="John Doe"
                      className="w-full px-4 py-3 border border-input bg-input-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-foreground mb-2"
                    >
                      Email Address *
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      placeholder="you@company.com"
                      className="w-full px-4 py-3 border border-input bg-input-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="company"
                      className="block text-foreground mb-2"
                    >
                      Company
                    </label>
                    <input
                      id="company"
                      type="text"
                      value={formData.company}
                      onChange={(e) =>
                        setFormData({ ...formData, company: e.target.value })
                      }
                      placeholder="Your Company Name"
                      className="w-full px-4 py-3 border border-input bg-input-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-foreground mb-2"
                    >
                      Subject *
                    </label>
                    <input
                      id="subject"
                      type="text"
                      value={formData.subject}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                      placeholder="How can we help?"
                      className="w-full px-4 py-3 border border-input bg-input-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-foreground mb-2"
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      placeholder="Tell us more about your inquiry..."
                      rows={5}
                      className="w-full px-4 py-3 border border-input bg-input-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full px-6 py-4 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity shadow-lg flex items-center justify-center gap-2"
                  >
                    <Send className="w-5 h-5" />
                    Send Message
                  </button>
                </form>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <div className="bg-card border border-card-border rounded-2xl p-8">
                <h2 className="mb-6 text-foreground">Contact Information</h2>

                <div className="space-y-4">
                  {contactInfo.map((info, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-4 p-4 bg-secondary rounded-lg"
                    >
                      <info.icon className="w-6 h-6 text-primary mt-1 shrink-0" />
                      <div>
                        <h3 className="mb-1 text-foreground">{info.title}</h3>
                        {info.action ? (
                          <a
                            href={info.action}
                            className="text-foreground-secondary hover:text-primary transition-colors"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-foreground-secondary">
                            {info.content}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-8 border-t border-card-border">
                  <h3 className="mb-4 text-foreground">Follow Us</h3>
                  <div className="flex gap-4">
                    <button className="w-12 h-12 bg-secondary hover:bg-background-secondary rounded-lg flex items-center justify-center transition-colors">
                      <Twitter className="w-5 h-5 text-foreground" />
                    </button>
                    <button className="w-12 h-12 bg-secondary hover:bg-background-secondary rounded-lg flex items-center justify-center transition-colors">
                      <Linkedin className="w-5 h-5 text-foreground" />
                    </button>
                    <button className="w-12 h-12 bg-secondary hover:bg-background-secondary rounded-lg flex items-center justify-center transition-colors">
                      <MessageSquare className="w-5 h-5 text-foreground" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-linear-to-br from-primary/10 to-[#8b5cf6]/10 border border-primary/20 rounded-2xl p-6">
                <h3 className="mb-3 text-foreground">Need Immediate Help?</h3>
                <p className="text-foreground-secondary mb-4">
                  Check out our comprehensive documentation and FAQs for instant
                  answers.
                </p>
                <button className="text-primary hover:opacity-80 transition-opacity">
                  Visit Help Center →
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Departments */}
      <section className="py-16 px-4 bg-card/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="mb-4 text-foreground">Contact by Department</h2>
            <p className="text-foreground-secondary max-w-2xl mx-auto">
              Reach out to the right team for faster assistance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {departments.map((dept, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-card border border-card-border rounded-xl p-6"
              >
                <h3 className="mb-2 text-foreground">{dept.title}</h3>
                <p className="text-foreground-secondary mb-3">
                  {dept.description}
                </p>
                <a
                  href={`mailto:${dept.email}`}
                  className="text-primary hover:opacity-80 transition-opacity inline-flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  {dept.email}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Callout */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-linear-to-br from-primary via-[#8b5cf6] to-[#ec4899] relative overflow-hidden rounded-2xl p-12"
          >
            {/* Grid Pattern Overlay */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>

            {/* Floating Sketched Elements */}
            {/* Star sketch top-left */}
            <motion.svg
              className="absolute top-10 left-10 w-16 h-16 text-white/40"
              animate={{
                y: [0, -20, 0],
                rotate: [0, 15, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              viewBox="0 0 100 100"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M50 10 L55 40 L85 45 L60 65 L70 95 L50 80 L30 95 L40 65 L15 45 L45 40 Z" />
              <circle cx="50" cy="50" r="3" fill="currentColor" />
            </motion.svg>

            {/* Lightning bolt sketch top-right */}
            <motion.svg
              className="absolute top-20 right-20 w-20 h-20 text-white/50"
              animate={{
                y: [0, 15, 0],
                x: [0, -10, 0],
              }}
              transition={{
                duration: 3.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.5,
              }}
              viewBox="0 0 100 100"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M55 10 L25 45 L45 48 L35 90 L75 45 L55 43 Z" />
            </motion.svg>

            {/* Checkmark circle sketch bottom-left */}
            <motion.svg
              className="absolute bottom-16 left-16 w-24 h-24 text-white/30"
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, -10, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1,
              }}
              viewBox="0 0 100 100"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="50" cy="50" r="40" strokeDasharray="5,5" />
              <path d="M30 50 L45 65 L70 35" strokeWidth="3" />
            </motion.svg>

            {/* Arrow sketch bottom-right */}
            <motion.svg
              className="absolute bottom-20 right-16 w-28 h-28 text-white/40"
              animate={{
                x: [0, 10, 0],
                y: [0, -10, 0],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 4.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1.5,
              }}
              viewBox="0 0 100 100"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10 50 Q50 10 90 50" />
              <path d="M70 30 L90 50 L70 70" />
              <circle cx="15" cy="48" r="3" fill="currentColor" />
            </motion.svg>

            {/* Sparkle sketch middle-left */}
            <motion.svg
              className="absolute top-1/2 left-1/4 w-12 h-12 text-white/60 hidden lg:block"
              animate={{
                scale: [1, 1.3, 1],
                rotate: [0, 180, 360],
                opacity: [0.6, 1, 0.6],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              viewBox="0 0 100 100"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M50 10 L50 90 M10 50 L90 50" />
              <path d="M20 20 L80 80 M80 20 L20 80" />
            </motion.svg>

            {/* Scribble circle middle-right */}
            <motion.svg
              className="absolute top-1/3 right-1/4 w-20 h-20 text-white/35 hidden lg:block"
              animate={{
                rotate: [0, 360],
                scale: [1, 1.15, 1],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 2,
              }}
              viewBox="0 0 100 100"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="50" cy="50" r="30" strokeDasharray="3,3" />
              <circle cx="50" cy="50" r="20" strokeDasharray="2,4" />
              <circle cx="50" cy="50" r="10" strokeDasharray="1,2" />
            </motion.svg>

            {/* Dotted heart sketch */}
            <motion.svg
              className="absolute bottom-1/3 left-1/3 w-16 h-16 text-white/45 hidden md:block"
              animate={{
                y: [0, -15, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.8,
              }}
              viewBox="0 0 100 100"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path
                d="M50 85 C50 85, 20 60, 20 40 C20 25, 30 15, 40 15 C45 15, 50 20, 50 20 C50 20, 55 15, 60 15 C70 15, 80 25, 80 40 C80 60, 50 85, 50 85 Z"
                strokeDasharray="4,4"
              />
            </motion.svg>

            {/* Plus signs */}
            <motion.svg
              className="absolute top-1/4 right-1/3 w-10 h-10 text-white/50 hidden lg:block"
              animate={{
                rotate: [0, 90, 0],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1.2,
              }}
              viewBox="0 0 100 100"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            >
              <path d="M50 20 L50 80 M20 50 L80 50" />
            </motion.svg>

            <div className="relative z-10">
              <MessageSquare className="w-12 h-12 text-white mx-auto mb-4" />
              <h2 className="mb-4 text-white font-bold">
                Looking for Quick Answers?
              </h2>
              <p className="text-white/95 mb-8 max-w-2xl mx-auto">
                Most common questions are answered in our documentation and
                knowledge base. Save time and find instant solutions.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <motion.button
                  className="px-8 py-4 bg-white text-primary rounded-lg hover:bg-white/95 transition-all shadow-xl hover:shadow-2xl flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Send className="w-5 h-5" />
                  Browse Documentation
                </motion.button>
                <motion.button
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <MessageSquare className="w-5 h-5" />
                  View FAQs
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
